import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// Connects to data-controller="manual-attendance-search"
// Wraps a search input plus an optional checked-in-at input, debounces typing,
// and loads filtered results into a turbo-frame. The additional input is
// forwarded as a URL param so that each result row's button_to POST can pick it
// up without a separate form submission.
export default class extends Controller {
  static targets = ["query", "checkedInAt", "dob"];
  static values = {
    url: String,
    frame: String,
    debounce: Number,
  };

  declare readonly queryTarget: HTMLInputElement;
  declare readonly checkedInAtTarget: HTMLInputElement;
  declare readonly hasCheckedInAtTarget: boolean;
  declare readonly dobTarget: HTMLInputElement;
  declare readonly hasDobTarget: boolean;

  declare urlValue: string;
  declare frameValue: string;
  declare debounceValue: number;

  defaultDebounce = 250;
  declare timeout: ReturnType<typeof setTimeout> | undefined;

  search() {
    clearTimeout(this.timeout);
    const delay = this.debounceValue || this.defaultDebounce;
    this.timeout = setTimeout(() => this.visit(), delay);
  }

  refresh() {
    // Fire immediately when sibling inputs change, but only when we already
    // have a query — otherwise there is nothing to show.
    if (this.queryTarget.value.trim().length === 0) return;
    this.visit();
  }

  // Empties every field and puts the frame back to its idle state, which the
  // kiosk needs between people — the next person in the line should not walk up
  // to the previous one's name still on screen.
  clear() {
    clearTimeout(this.timeout);
    this.queryTarget.value = "";
    if (this.hasDobTarget) this.dobTarget.value = "";
    this.visit();
  }

  visit() {
    const url = new URL(this.urlValue, document.baseURI);
    url.searchParams.set("q", this.queryTarget.value);

    if (this.hasCheckedInAtTarget && this.checkedInAtTarget.value) {
      url.searchParams.set("checked_in_at", this.checkedInAtTarget.value);
    }

    // Only narrows an already-matching set, so a partial date is simply
    // forwarded and left for the server to parse or ignore.
    if (this.hasDobTarget && this.dobTarget.value) {
      url.searchParams.set("dob", this.dobTarget.value);
    }

    const frame = document.getElementById(this.frameValue);
    if (frame) {
      frame.setAttribute("src", url.toString());
      return;
    }

    Turbo.visit(url.toString(), { action: "replace" });
  }
}
